import React, { useState } from 'react';
import { useCalendar } from '../../contexts/CalendarContext';
import { ChevronDown, ChevronRight, Building2, LayoutGrid, Target, Users, Globe } from 'lucide-react';
import styles from './CalendarSelector.module.css';

// Scope icons mapping - modern lucide-react icons
const SCOPE_ICONS = {
  club: <Building2 size={16} />,
  branch: <LayoutGrid size={16} />,
  program: <Target size={16} />,
  team: <Users size={16} />,
  platform: <Globe size={16} />
};

// Scope display order
const SCOPE_ORDER = ['club', 'program', 'branch', 'team', 'platform'];

const CalendarSelector = () => {
  const {
    availableCalendars,
    selectedCalendarIds,
    toggleCalendar,
    toggleAllCalendars,
    isCalendarSelected,
    getEventsForCalendar,
    events
  } = useCalendar();

  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedScopes, setExpandedScopes] = useState({
    club: true,
    program: true,
    branch: true,
    team: true,
    platform: true
  });

  const allSelected = availableCalendars.length > 0 &&
    selectedCalendarIds.length === availableCalendars.length;

  const someSelected = selectedCalendarIds.length > 0 &&
    selectedCalendarIds.length < availableCalendars.length;

  const handleToggleAll = () => {
    toggleAllCalendars(!allSelected);
  };

  const getEventCount = (calendarId) => {
    return getEventsForCalendar(calendarId).length;
  };

  // Group calendars by scope
  const groupCalendarsByScope = () => {
    const grouped = {};

    availableCalendars.forEach(calendar => {
      const scope = calendar.scope || calendar.calendar_scope || 'other';
      if (!grouped[scope]) {
        grouped[scope] = [];
      }
      grouped[scope].push(calendar);
    });

    return grouped;
  };

  const toggleScopeExpanded = (scope) => {
    setExpandedScopes(prev => ({
      ...prev,
      [scope]: !prev[scope]
    }));
  };

  const groupedCalendars = groupCalendarsByScope();

  return (
    <div className={styles.calendarUselector}>
      <div className={styles.calendarUselectorUheader}>
        <h3 className={styles.calendarUselectorUtitle}>Calendars</h3>
        <button
          className={styles.calendarUselectorUtoggle}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
      </div>

      {isExpanded && (
        <div className={styles.calendarUselectorUcontent}>
          <div className={styles.calendarUselectorUactions}>
            <label className="calendar-checkbox-item select-all">
              <input
                type="checkbox"
                checked={allSelected}
                ref={input => {
                  if (input) {
                    input.indeterminate = someSelected;
                  }
                }}
                onChange={handleToggleAll}
              />
              <span className={styles.calendarUcheckboxUlabel}>
                {allSelected ? 'Deselect All' : 'Select All'}
              </span>
              <span className={styles.calendarUcount}>
                ({selectedCalendarIds.length}/{availableCalendars.length})
              </span>
            </label>
          </div>

          <div className={styles.calendarUlist}>
            {availableCalendars.length === 0 ? (
              <div className={styles.calendarUemptyUstate}>
                <p>No calendars available</p>
              </div>
            ) : (
              // Render calendars grouped by scope
              SCOPE_ORDER.map(scope => {
                const calendarsInScope = groupedCalendars[scope] || [];
                if (calendarsInScope.length === 0) return null;

                const scopeLabel = scope.charAt(0).toUpperCase() + scope.slice(1);
                const isScopeExpanded = expandedScopes[scope];

                return (
                  <div key={scope} className={styles.calendarUscopeUgroup}>
                    {/* Scope Header */}
                    <div
                      className={styles.calendarUscopeUheader}
                      onClick={() => toggleScopeExpanded(scope)}
                    >
                      <span className={styles.calendarUscopeUicon}>{SCOPE_ICONS[scope]}</span>
                      <span className={styles.calendarUscopeUlabel}>
                        {scopeLabel} Calendars
                      </span>
                      <span className={styles.calendarUscopeUcount}>
                        ({calendarsInScope.length})
                      </span>
                      <span className={styles.calendarUscopeUtoggle}>
                        {isScopeExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </span>
                    </div>

                    {/* Calendars in this scope */}
                    {isScopeExpanded && (
                      <div className={styles.calendarUscopeUitems}>
                        {calendarsInScope.map((calendar) => {
                          const isSelected = isCalendarSelected(calendar.id);
                          const eventCount = getEventCount(calendar.id);
                          const colorCode = calendar.color_code || 'var(--color-info)';

                          return (
                            <label
                              key={calendar.id}
                              className={`${styles.calendarCheckboxItem} ${isSelected ? styles.selected : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleCalendar(calendar.id)}
                              />
                              <span
                                className={styles.calendarUcolorUindicator}
                                style={{ backgroundColor: colorCode }}
                              />
                              <span className={styles.calendarUcheckboxUlabel}>
                                {calendar.name}
                              </span>
                              {isSelected && eventCount > 0 && (
                                <span className={styles.calendarUeventUcount}>
                                  {eventCount}
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className={styles.calendarUselectorUfooter}>
            <div className={styles.calendarUtypesUsummary}>
              {availableCalendars
                .reduce((acc, cal) => {
                  const type = cal.calendar_scope || 'other';
                  acc[type] = (acc[type] || 0) + 1;
                  return acc;
                }, {})
                && Object.entries(
                  availableCalendars.reduce((acc, cal) => {
                    const type = cal.calendar_scope || 'other';
                    acc[type] = (acc[type] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([type, count]) => (
                  <span key={type} className={styles.calendarUtypeUtag}>
                    {type.replace(/_/g, ' ')}: {count}
                  </span>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarSelector;
